/**
 * kHtmlSafeWalk - the DOM walk of kHtmlSafeCore.Clean: keeps allowed elements and attributes, drops the rest.
 * Split out of kHtmlSafeCore.ts (2026-10-19, owner: smaller files for the reuse reviewer). kHtmlSafeCore.ts
 * re-exports every public name, so importers of "./kHtmlSafeCore" are unchanged.
 */

import { kHtmlParserCore } from "./kHtmlParserCore";
import type { IkDomElement, IkDomNode, IkDomTextNode } from "./kHtmlParserCore";
import { getCoreUtils } from "./kCoreHandler";
import type { kHtmlSafePolicy } from "./kHtmlSafeCore";
import { kHtmlSafeAllowList } from "./kHtmlSafeAllowList";
import { kHtmlSafeValueCore } from "./kHtmlSafeValueCore";


/** One Clean run: copies the allowed part of a parsed tree into a fresh one, counting what it drops. */
export class kHtmlSafeWalk {
    public Policy: kHtmlSafePolicy;
    public Dropped: number = 0;
    public Nodes: number = 0;
    public Deepest: number = 0;

    public constructor(Policy: kHtmlSafePolicy) {
        this.Policy = Policy;
    }

    /** Counts the source tree's nodes and its deepest element (a top-level element is depth 1). */
    public Measure = (El: IkDomElement, Depth: number): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            for (const Kid of El.childNodes) {
                this.Nodes = this.Nodes + 1;
                if (Kid.nodeType !== 1) { continue; }
                if (Depth + 1 > this.Deepest) { this.Deepest = Depth + 1; }
                if (this.Nodes > this.Policy.MaxNodes || this.Deepest > this.Policy.MaxDepth) { return; }
                this.Measure(Kid as IkDomElement, Depth + 1);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.Measure");
        }
    };

    /** Copies Src's allowed children into Dst. `Rule` is Dst's content model: "html", or an SvgContent word. */
    public CopyChildren = (Src: IkDomElement, Dst: IkDomElement, Rule: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            for (const Kid of Src.childNodes) {
                if (Kid.nodeType === 3) { this.CopyText(Kid, Dst, Rule); continue; }
                if (Kid.nodeType !== 1) { continue; }
                const Copy: IkDomElement | null = this.CopyElement(Kid as IkDomElement, Rule);
                if (Copy === null) { this.Dropped = this.Dropped + 1; continue; }
                Copy.parentNode = Dst;
                Dst.childNodes.push(Copy);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.CopyChildren");
        }
    };

    /** A text node is kept as plain text where the content model holds text; elsewhere in SVG it goes. */
    private CopyText = (Src: IkDomNode, Dst: IkDomElement, Rule: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (Rule !== "html" && Rule !== "text" && Rule !== "textonly") { return; }
            const Text: string = Src.nodeValue || "";
            if (Text === "") { return; }
            // Built directly, NOT with kHtmlParserCore.CreateTextNode: that decodes entities, and this
            // text was decoded once already - "&amp;lt;" must stay the text "&lt;", never become "<".
            const Node: IkDomTextNode = { nodeType: 3, nodeValue: Text, parentNode: Dst };
            Dst.childNodes.push(Node);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.CopyText");
        }
    };

    /** The cleaned copy of one element (and its subtree), or null to drop it whole. */
    private CopyElement = (Src: IkDomElement, ParentRule: string): IkDomElement | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            const Lower: string = Src.tagName.toLowerCase();
            if (/^[a-z][a-z0-9]{0,31}$/.test(Lower) === false) { return null; }
            if (kHtmlSafeAllowList.DeniedTags.indexOf(Lower) >= 0) { return null; }
            if (ParentRule === "html") {
                if (Lower === "svg") { return this.CopySvgRoot(Src); }
                const HtmlName: string = kHtmlSafeWalk.CanonicalIn(this.Policy.HtmlTags, Lower);
                if (HtmlName === "") { return null; }
                return this.Build(Src, HtmlName, false, "html");
            }
            const SvgName: string = kHtmlSafeWalk.CanonicalIn(this.Policy.SvgTags, Lower);
            if (SvgName === "" || kHtmlSafeWalk.SvgChildAllowed(ParentRule, SvgName) === false) { return null; }
            return this.Build(Src, SvgName, true, kHtmlSafeWalk.SvgRuleOf(SvgName));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.CopyElement");
            return null;
        }
    };

    /** An <svg> met in HTML content: allowed only if the policy lists it, and (by default) only
     *  with a valid viewBox. */
    private CopySvgRoot = (Src: IkDomElement): IkDomElement | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            if (kHtmlSafeWalk.CanonicalIn(this.Policy.SvgTags, "svg") === "") { return null; }
            const Svg: IkDomElement = this.Build(Src, "svg", true, "container");
            if (this.Policy.RequireSvgViewBox === true && Svg.attributes["viewbox"] === undefined) { return null; }
            return Svg;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.CopySvgRoot");
            return null;
        }
    };

    /** A fresh element named Name, with Src's allowed attributes and children. */
    private Build = (Src: IkDomElement, Name: string, IsSvg: boolean, Rule: string): IkDomElement => {
        if (getCoreUtils().KErrorMode) { return kHtmlParserCore.CreateElement(Name); }
        try {
            const Out: IkDomElement = kHtmlParserCore.CreateElement(Name);
            this.CopyAttributes(Src, Out, Name, IsSvg);
            this.CopyChildren(Src, Out, Rule);
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.Build");
            return kHtmlParserCore.CreateElement(Name);
        }
    };

    /** Every attribute that passes the hard denies, the policy table and its value check, under its
     *  canonical name. */
    private CopyAttributes = (Src: IkDomElement, Out: IkDomElement, Tag: string, IsSvg: boolean): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            let Table: Record<string, string> = this.Policy.HtmlAttributes;
            if (IsSvg === true) { Table = this.Policy.SvgAttributes; }
            for (const Key of Object.keys(Src.attributes)) {
                const Name: string = kHtmlSafeWalk.CanonicalIn(Object.keys(Table), Key);
                const Value: string | null = this.AttributeValue(Name, Key, Src.attributes[Key], Tag, Table);
                if (Value === null) { this.Dropped = this.Dropped + 1; continue; }
                Out.attributes[Name.toLowerCase()] = Value;
                if (Out.attributeNames !== undefined) { Out.attributeNames[Name.toLowerCase()] = Name; }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.CopyAttributes");
        }
    };

    /** The value to keep for one attribute, or null. `Name` is the canonical name ("" = not in the table). */
    private AttributeValue = (Name: string, Key: string, Value: string, Tag: string,
                              Table: Record<string, string>): string | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            if (kHtmlSafeWalk.IsDeniedAttribute(Key) === true || Name === "") { return null; }
            const Only: string[] | undefined = this.Policy.AttributeOnly[Name];
            if (Only !== undefined && Only.indexOf(Tag) < 0) { return null; }
            if (kHtmlSafeValueCore.IsHostileValue(Value) === true) { return null; }
            return kHtmlSafeValueCore.Check(Table[Name], Value, this.Policy);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.AttributeValue");
            return null;
        }
    };

    /** Names no policy may admit: event handlers, anything namespaced (xlink:href, xml:base), the deny list. */
    public static IsDeniedAttribute = (Lower: string): boolean => {
        if (getCoreUtils().KErrorMode) { return true; }
        try {
            if (Lower.indexOf("on") === 0 || Lower.indexOf(":") >= 0) { return true; }
            if (/^[a-z][a-z0-9-]{0,63}$/.test(Lower) === false) { return true; }
            return kHtmlSafeAllowList.DeniedAttributes.indexOf(Lower) >= 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.IsDeniedAttribute");
            return true;
        }
    };

    /** The entry of Allowed matching Lower case-insensitively, in its canonical spelling; "" if none. */
    public static CanonicalIn = (Allowed: string[], Lower: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            for (const Name of Allowed) {
                if (Name.toLowerCase() === Lower) { return Name; }
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.CanonicalIn");
            return "";
        }
    };

    /** The content model of an SVG element; an element the table does not know holds nothing. */
    public static SvgRuleOf = (Name: string): string => {
        if (getCoreUtils().KErrorMode) { return "empty"; }
        try {
            const Rule: string | undefined = kHtmlSafeAllowList.SvgContent[Name];
            if (Rule === undefined) { return "empty"; }
            return Rule;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.SvgRuleOf");
            return "empty";
        }
    };

    /** Whether an SVG element named Child may sit in a parent whose content model is ParentRule. An
     *  <svg> is never allowed inside SVG: it must be the root of its SVG subtree. */
    public static SvgChildAllowed = (ParentRule: string, Child: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (Child === "svg") { return false; }
            if (ParentRule === "container") { return Child !== "stop" && Child !== "tspan"; }
            if (ParentRule === "text") { return Child === "tspan"; }
            if (ParentRule === "gradient") { return Child === "stop"; }
            if (ParentRule === "shape") { return Child === "title" || Child === "desc"; }
            return false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kHtmlSafeWalk.SvgChildAllowed");
            return false;
        }
    };
}
